import React, { useCallback, useEffect, useRef } from "react"
import { Message, ThreadMessages } from "./inAppMessagingTypes"
import { CenteredLoadingIndicator } from "../views/molecules/Molecules"
import { reportError } from "../../utils/ErrorReporting"
import useGetThreadMessages from "./hooks/useGetThreadMessages"
import { DisclaimerMessage } from "./MessagingDisclaimer"
import useMarkThreadAsRead from "./hooks/useMarkThreadAsRead"
import { useInAppMessaging } from "../context/messaging/InAppMessagingContext"
import { ClosedThreadMessage } from "./ClosedThreadMessage"
import { getParsedHtmlElements } from "./utils"
import { FormattedTime, FormattedMessage, useIntl } from "react-intl"
import { getPiiProps } from "#/services/tracking"
import { Box, Link, Stack, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import { EmployeeSupportUrl } from "../../utils/EmployeeSupportLink"

type MessageReplyBubbleProps = {
  message: Message
}

function MessageReplyBubble({ message }: MessageReplyBubbleProps): JSX.Element {
  const theme = useTheme()

  return (
    <Box
      data-testid="message-bubble"
      sx={(theme) => ({
        // MOVED HERE FROM MUI SYSTEM PROPS (`borderRadius`, `padding`, `bgcolor`), which are deprecated.
        // Two more came across and are deliberately absent: `flexDirection: "column"` and
        // `alignItems: "start"`. Nothing sets `display` on this box, so it is a block container and both
        // were inert. The one self-alignment that is NOT inert is below — it acts on the parent's column.
        //
        // THE RADIUS TOKEN AS A NUMBER. `borderRadius` in `sx` multiplies by `shape.borderRadius` (4), so
        // `md` (2) is 8px — the same 8px the system prop produced, both paths running through the same
        // style function. `theme.borderRadius(...)` here would be 32px, and a test pins the 8px.
        borderRadius: theme.tokens.borderRadius.md,
        padding: theme.spacing(theme.tokens.spacing.lg),
        backgroundColor: message.authoredBy === "CARROT" ? theme.palette.primary.light : theme.palette.background.paper,

        // AS WIDE AS ITS OWN CONTENT, up to 84% of the pane, the care team against the start edge and the
        // member against the end — the same rule the Providers tab follows
        // (packages/messages/src/components/Messages.tsx). Both bubbles used to run the pane's full
        // width, so a conversation was one column of bands with only a name and a tint to tell the two
        // voices apart.
        //
        // 84% is the design's, and is a proportion, which no token is. Both sides are stated rather than
        // left to the column's default, so an `align-items` added to the parent cannot quietly re-align
        // them.
        //
        // `align-self` needs the flex column this sits in — see `ThreadMessagesContainer` below, which was
        // a block container until this arrived, and where both of these declarations are inert without it.
        alignSelf: message.authoredBy === "CARROT" ? "flex-start" : "flex-end",
        maxInlineSize: "84%"
      })}
    >
      <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.xs)} alignItems="center">
        {message.authoredBy === "CARROT" ? (
          <Typography variant="body1" fontWeight="bold" color={theme.palette.primary.accent}>
            <FormattedMessage defaultMessage="Care Team" />
          </Typography>
        ) : (
          <Typography variant="body1" fontWeight="bold" color={theme.palette.text.secondary}>
            <FormattedMessage defaultMessage="You" />
          </Typography>
        )}
        <Typography
          variant="body1"
          color={message.authoredBy === "CARROT" ? theme.palette.primary.accent : theme.palette.text.tertiary}
        >
          <FormattedTime value={message.createdAt} />
        </Typography>
      </Stack>
      <Box
        {...getPiiProps()}
        padding={(theme) => `${theme.spacing(theme.tokens.spacing.lg)} ${theme.spacing(theme.tokens.spacing.md)}`}
        flexDirection="column"
        sx={{
          whiteSpace: "pre-line",
          lineHeight: theme.typography.body1.lineHeight,
          wordBreak: "break-word",
          ...theme.typography.body1
        }}
      >
        {getParsedHtmlElements(message.htmlBody)}
      </Box>
    </Box>
  )
}
export function MessagingErrorContainer(): JSX.Element {
  return (
    <Tile>
      <Stack direction={"column"} spacing={"small"}>
        <Typography variant="h3" component="h2">
          <FormattedMessage defaultMessage="Something's not working" />
        </Typography>
        <Typography variant="body1">
          <FormattedMessage
            defaultMessage="Try reloading this page. If the problem persists, feel free to <link>contact us</link>."
            values={{
              link: (linkContent) => (
                <Link href={EmployeeSupportUrl} target={"_blank"}>
                  {linkContent}
                </Link>
              )
            }}
          />
        </Typography>
      </Stack>
    </Tile>
  )
}

type ScrollToNewestMessageProps = {
  /** Which conversation is open — opening one lands the member on its newest message. */
  threadId: number | undefined
  /**
   * Changes on one event only: a message newer than the one already rendered. The legacy payload
   * carries no message id, so this is built from the count and the newest timestamp.
   */
  newestMessageKey: string | null
}

/**
 * Keeps the newest message in view.
 *
 * MUST STAY AT MODULE SCOPE. Declared inside `ThreadMessagesContainer` it would be a different
 * component type on every render, so React would remount it and re-run the effect below every time
 * the pane renders for any reason — scrolling the member away from what they were reading.
 */
function ScrollToNewestMessage({ threadId, newestMessageKey }: ScrollToNewestMessageProps): JSX.Element {
  const elementRef = useRef<HTMLDivElement>()
  useEffect(() => {
    // Use a block body: scrollIntoView() now returns a Promise in recent Chrome, and implicitly
    // returning it here would make React treat it as the effect cleanup ("destroy is not a
    // function" crash on unmount).
    elementRef.current?.scrollIntoView({ block: "nearest", inline: "start" })
  }, [threadId, newestMessageKey])
  return <div ref={elementRef} />
}

export function ThreadMessagesContainer(): JSX.Element {
  const intl = useIntl()

  const { threadUpdateInFlight, selectedThread, threadIdForFailedUpdate } = useInAppMessaging()
  const {
    isLoading: isLoadingMessages,
    error,
    data,
    isSuccess: messagesFetchedSuccessfully
  } = useGetThreadMessages(selectedThread?.threadId)
  const { mutate: markThreadAsRead } = useMarkThreadAsRead()
  const theme = useTheme()

  useEffect(() => {
    if (selectedThread?.hasUnreadMessages && !isLoadingMessages && messagesFetchedSuccessfully) {
      markThreadAsRead(selectedThread?.threadId)
    }
  }, [
    selectedThread?.hasUnreadMessages,
    messagesFetchedSuccessfully,
    isLoadingMessages,
    selectedThread?.threadId,
    markThreadAsRead
  ])

  const getMappedMessageBubbles = useCallback(
    (data: ThreadMessages) => {
      let lastUsedDate: string | null = null

      function dateHelper(lastUsedDate: string | null, currentDate: string): string | null {
        if (lastUsedDate === currentDate) return null
        if (currentDate === intl.formatDate(new Date(), { month: "short", day: "numeric" })) {
          return intl.formatRelativeTime(0, "day", { numeric: "auto" })
        } else {
          return currentDate
        }
      }

      return [...data.messages].reverse().map((message, idx) => {
        const currentDate = intl.formatDate(message.createdAt, { month: "short", day: "numeric" })
        const dateToDisplay = dateHelper(lastUsedDate, currentDate)
        if (dateToDisplay) lastUsedDate = currentDate
        return (
          <React.Fragment key={idx}>
            <Box display="flex" justifyContent="center" paddingY={theme.spacing(theme.tokens.spacing.md)}>
              <Typography variant="overline" color={theme.palette.text.tertiary}>
                {dateToDisplay}
              </Typography>
            </Box>
            <MessageReplyBubble message={message} />
          </React.Fragment>
        )
      })
    },
    [intl, theme]
  )

  // `data.messages` arrives NEWEST FIRST — the render below reverses it — so the newest message is
  // the head of the list.
  const newestMessage = data?.messages?.[0]
  const newestMessageKey = newestMessage ? `${data.messages.length}-${String(newestMessage.createdAt)}` : null

  // Must report from an effect keyed on the ERROR'S IDENTITY, never from the error branch's render
  // body. This pane re-renders whenever the thread list churns beneath it, and reporting during
  // render files a fresh Sentry report on each one; the messages query has no `refetchInterval`, so
  // it fails once and keeps handing back the same error object, which is what makes the identity key
  // once-per-failure.
  const threadIdForReport = selectedThread?.threadId?.toString()
  useEffect(() => {
    if (!error) {
      return
    }
    reportError(error, threadIdForReport ? { threadId: threadIdForReport } : undefined)
  }, [error, threadIdForReport])

  if (isLoadingMessages) {
    return <CenteredLoadingIndicator />
  }
  if (error) {
    return (
      <Box marginInlineStart={theme.spacing(theme.tokens.spacing.xl)}>
        <MessagingErrorContainer />
      </Box>
    )
  }
  if (data) {
    return (
      <Box
        sx={(theme) => ({
          paddingBlockStart: theme.spacing(theme.tokens.spacing.sm),
          // `paddingInline` and `marginBlockEnd`, where the system props they replace were `paddingX` and
          // `marginBottom`. Same values, written on the axes the rest of this repo uses.
          paddingInline: theme.spacing(theme.tokens.spacing.sm),
          marginBlockEnd: theme.spacing(theme.tokens.spacing.sm),

          // A FLEX COLUMN, so the bubbles' `align-self` means something — in a block container it is inert
          // and they fill the pane, which is what they used to do. Everything else in here is full-width
          // and stays that way under the default `align-items: stretch`: the date separators, the
          // disclaimer, the closed-thread notice, the failure line.
          display: "flex",
          flexDirection: "column"
        })}
      >
        <DisclaimerMessage />
        {getMappedMessageBubbles(data)}
        {selectedThread?.isClosed && <ClosedThreadMessage />}
        {threadIdForFailedUpdate === selectedThread?.threadId && (
          <Typography
            color={theme.palette.error.main}
            variant="body2"
            paddingTop={theme.spacing(theme.tokens.spacing.sm)}
          >
            <FormattedMessage
              defaultMessage="Your message failed to send. Please try again or <link>contact us</link>."
              values={{
                link: (linkContent) => (
                  <Link
                    color="inherit"
                    href={EmployeeSupportUrl}
                    target={"_blank"}
                    sx={{ fontSize: theme.typography.body2.fontSize }}
                  >
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
        )}
        {threadUpdateInFlight && <CenteredLoadingIndicator />}
        <ScrollToNewestMessage threadId={selectedThread?.threadId} newestMessageKey={newestMessageKey} />
      </Box>
    )
  }

  return null
}
